import { FC, ReactNode, RefObject, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';

import { AsCSR } from '@telialt/common-as-csr/src';
import { KeyboardCode } from '@telialt/common-constants/src/keyboard-codes';
import { isSSR } from '@telialt/utils/src/helpers';

import { useKeyboardFocus } from '../../hooks/useKeyboardFocus';
import { DrawerContainer, DrawerOverlay } from './MenuDrawer.styles';

type Props = {
    id: string;
    buttonRef?: RefObject<HTMLButtonElement>;
    isVisible: boolean;
    onClose: () => void;
    children?: ReactNode;
};

// This component will only be rendered in client side (browser only)
const MenuDrawerContent: FC<Props> = ({ id, buttonRef, isVisible, onClose, children }) => {
    const drawerRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useKeyboardFocus(drawerRef.current);

    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    useEffect(() => {
        if (isVisible) {
            window.setTimeout(() => setIsOpen(true), 50);
        } else {
            window.setTimeout(() => setIsOpen(false), 500);
        }
    }, [isVisible]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const targetElement = event.target as HTMLElement;
            if (
                isOpenRef.current &&
                (!drawerRef.current || !drawerRef.current.contains(targetElement)) &&
                (!buttonRef?.current || !buttonRef.current.contains(targetElement))
            ) {
                onCloseRef.current();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [buttonRef]);

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.code === KeyboardCode.Escape && isOpenRef.current) {
                onCloseRef.current();
            }
        };

        window.addEventListener('keydown', handleEscKey);

        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, []);

    useEffect(() => {
        const drawerElement = drawerRef.current;
        const handleFocusOut = (event: FocusEvent) => {
            if (
                event.relatedTarget &&
                !drawerElement.contains(event.relatedTarget as HTMLElement) &&
                !buttonRef?.current?.contains(event.relatedTarget as HTMLElement)
            ) {
                onCloseRef.current();
            }
        };
        drawerElement.addEventListener('focusout', handleFocusOut);

        return () => {
            drawerElement.removeEventListener('focusout', handleFocusOut);
        };
    }, [buttonRef]);

    const handleTransitionEnd = () => {
        if (!isVisible) {
            setIsOpen(false);
        }
    };

    if (isSSR()) {
        return null;
    }

    return (
        <>
            {createPortal(<DrawerOverlay $isVisible={isVisible && isOpen} />, document.body)}
            <RemoveScroll enabled={isVisible && isOpen}>
                <DrawerContainer
                    ref={drawerRef}
                    id={id}
                    $isOpen={isVisible && isOpen}
                    $isVisible={isVisible || isOpen}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {children}
                </DrawerContainer>
            </RemoveScroll>
        </>
    );
};

export const MenuDrawer: FC<Props> = (props) => {
    return (
        <AsCSR>
            <MenuDrawerContent {...props} />
        </AsCSR>
    );
};
